/**
 * EphemeralTokenContext - Manages ephemeral token state from passkey authentication
 *
 * Stores the short-lived token received after successful passkey auth.
 * This token is used for sensitive API calls (QRG envelopes, secure operations).
 *
 * Security notes:
 * - Token is stored in memory only (not localStorage/sessionStorage)
 * - Token expires per server rules (typically 5-15 minutes)
 * - Auto-clears on window unload for security
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import type { EphemeralToken } from '../lib/crypto/deviceKeys'

interface EphemeralTokenContextValue {
  token: EphemeralToken | null
  setToken: (token: EphemeralToken | null) => void
  isAuthenticated: boolean
  isExpired: boolean
  expiresIn: number | null // seconds remaining
  clear: () => void
}

const EphemeralTokenContext = createContext<EphemeralTokenContextValue | undefined>(undefined)

interface EphemeralTokenProviderProps {
  children: ReactNode
}

export function EphemeralTokenProvider({ children }: EphemeralTokenProviderProps) {
  const [token, setTokenState] = useState<EphemeralToken | null>(null)
  const [expiresIn, setExpiresIn] = useState<number | null>(null)

  // Check if token is expired
  const isExpired = token ? new Date(token.expiresAt) <= new Date() : false
  const isAuthenticated = token !== null && !isExpired

  // Update expiration countdown
  useEffect(() => {
    if (!token) {
      setExpiresIn(null)
      return
    }

    const updateExpiry = () => {
      const now = new Date()
      const expiry = new Date(token.expiresAt)
      const remaining = Math.max(0, Math.floor((expiry.getTime() - now.getTime()) / 1000))
      setExpiresIn(remaining)

      // Auto-clear if expired
      if (remaining === 0) {
        setTokenState(null)
      }
    }

    updateExpiry()
    const interval = setInterval(updateExpiry, 1000)
    return () => clearInterval(interval)
  }, [token])

  // Clear on window unload for security
  useEffect(() => {
    const handleUnload = () => {
      setTokenState(null)
    }
    window.addEventListener('beforeunload', handleUnload)
    return () => window.removeEventListener('beforeunload', handleUnload)
  }, [])

  const setToken = useCallback((newToken: EphemeralToken | null) => {
    setTokenState(newToken)
  }, [])

  const clear = useCallback(() => {
    setTokenState(null)
  }, [])

  const value: EphemeralTokenContextValue = {
    token,
    setToken,
    isAuthenticated,
    isExpired,
    expiresIn,
    clear,
  }

  return (
    <EphemeralTokenContext.Provider value={value}>
      {children}
    </EphemeralTokenContext.Provider>
  )
}

export function useEphemeralToken(): EphemeralTokenContextValue {
  const context = useContext(EphemeralTokenContext)
  if (!context) {
    throw new Error('useEphemeralToken must be used within an EphemeralTokenProvider')
  }
  return context
}

/**
 * Hook to get just the token string for API calls
 */
export function useEphemeralTokenString(): string | undefined {
  const { token, isAuthenticated } = useEphemeralToken()
  return isAuthenticated ? token?.token : undefined
}
